import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { styled } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import APIs from '../../../api';
import { CustomPagination, NoRowsOverlay } from '../../../components/Layout';
import { useTranslation } from '../../../hooks';
import useLoading from '../../../hooks/useLoading';
import { toDisplayTime } from '../../../utils';
import { useGenGridCol } from '../../../utils/ComponentHelper';
import { dataGridDefaults } from '../../../utils/constant';
import { ApproveHistory } from '../types/ApproveHistory';
import { useAppSelector } from '../../../reducer/hooks';
import { selectIsMobileView } from '../../../reducer/mediaSlice';
import CardList from '../../layout/Card/CardList';
import CopyableText from '../../../components/CopyableText';

export enum EnumApprovalStatus {
  Rejected = -1,
  Pending = 1,
  Approved = 2,
}

interface ApprovalProgressDialogContentProps {
  id?: string;
  isShowPendingAfterRejected?: boolean;
  approveHistory?: ApproveHistory[];
  getApproveHistory: ({ id }: { id: string }) => Promise<ApproveHistory[]>;
}

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  '& .MuiDataGrid-columnHeaders': {
    //Table Column style
    backgroundColor: theme.palette.secondary.main,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
      borderRight: `unset`,
    },
  },
}));

const ApprovalProgressDialogContent = (props: ApprovalProgressDialogContentProps) => {
  const { isShowPendingAfterRejected, id, getApproveHistory } = props;

  const { t } = useTranslation('enumConstants');
  const { showLoading, hideLoading } = useLoading();
  const isMobile = useAppSelector(selectIsMobileView);

  const fetchApproveHistory = async () => {
    if (!id) return;

    showLoading('getApproveHistory');
    const res = await getApproveHistory({ id });
    hideLoading('getApproveHistory');

    if (!res) return [];

    return res;
  };

  const { data } = useQuery(`approveHistory-${id}`, fetchApproveHistory, {
    onSuccess: (data) => {
      setApproveHistory(data);
    },
  });

  const [approveHistory, setApproveHistory] = useState<ApproveHistory[] | undefined>(data);

  const renderOperationResultCell = (params: any) => {
    const { row } = params;

    const colorMapping = {
      [EnumApprovalStatus.Approved]: '#4caf50',
      [EnumApprovalStatus.Rejected]: '#f44336',
      [EnumApprovalStatus.Pending]: '#ff9800',
    };
    return (
      <div style={{ color: colorMapping[row?.rawData?.status as EnumApprovalStatus] }}>
        {t(EnumApprovalStatus[row?.rawData?.status as EnumApprovalStatus])}
      </div>
    );
  };

  const columns = [
    useGenGridCol('id'),
    useGenGridCol('operation_result', {
      renderCell: renderOperationResultCell,
    }),
    useGenGridCol('operated_by'),
    useGenGridCol('operation_time'),
    useGenGridCol('remarks'),
  ];

  const createTableRows = (data?: ApproveHistory[]) => {
    if (!data || data?.length === 0) {
      return [];
    }

    return data.map((item, index) => {
      return {
        id: item.id,
        operation_result: t(EnumApprovalStatus[item.status]),
        operated_by: item.approvedBy,
        operation_time: toDisplayTime(item.approvedDate),
        remarks: item.remarks,
        rawData: item,
      };
    });
  };

  const displayApprovalArr = useMemo(() => {
    if (isShowPendingAfterRejected) {
      return approveHistory;
    }

    const filteredApprovalArr = approveHistory?.filter(({ status }) => {
      if (approveHistory.find(({ status }) => status === EnumApprovalStatus.Rejected)) {
        return status !== EnumApprovalStatus.Pending;
      }
      return true;
    });

    return filteredApprovalArr;
  }, [approveHistory, isShowPendingAfterRejected]);

  const rows = createTableRows(displayApprovalArr);

  if (isMobile) {
    return (
      <>
        <CardList rows={rows} columns={columns} />
      </>
    );
  }
  return (
    <StyledDataGrid
      {...dataGridDefaults}
      rows={rows}
      rowCount={displayApprovalArr?.length || 0}
      columns={columns}
      page={0}
      components={{
        NoRowsOverlay,
        Footer: CustomPagination,
      }}
      componentsProps={{
        footer: { totalRecords: displayApprovalArr?.length },
      }}
      initialState={{
        sorting: {
          sortModel: [{ field: 'approval_sequence', sort: 'asc' }],
        },
      }}
      hideFooter={true}
    />
  );
};

export default ApprovalProgressDialogContent;
